<template>
  <nav class="school-navbar">
    <div class="nav-container">
      <div class="hstack gap-3 text-white me-auto" data-v-step="2">
        <figure class="logo d-none d-sm-flex align-items-center m-0 me-3" alt="PTC Wizard Logo">
          <Logo class="h-100" />
        </figure>
      </div>

      <div class="nav-right">
        <div style="width: 150px"></div>

        <!-- QuickBooks connection status button — shammai only -->
        <button v-if="isQbAdmin" class="help fs-6" @click="qbModalOpen = true" title="QuickBooks">
          <QuickbooksIcon :connected="qbConnected" :size="20" />
          <span class="d-none d-sm-block">QuickBooks</span>
        </button>

        <button class="help fs-6" @click="tour?.start?.()">
          <i class="bi fs-5 bi-question-circle"></i>
          <span class="d-none d-sm-block">Help</span>
        </button>
        <div class="dropdown" data-cy="nav-select">
          <button
            class="btn text-white dropdown-toggle text-capitalize fs-normal px-2"
            type="button"
            id="dropdownMenuButton1"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i class="bi bi-person-fill fs-4 me-n1 d-md-none"></i>
            <span class="d-none d-md-inline-block align-middle">
              Support Agent              
            </span>
          </button>
          <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="dropdownMenuButton1">
            <li style="cursor: pointer">
              <RouterLink
                style="text-decoration: none; color: unset"
                :to="{ name: 'support-accounts' }"
                data-cy="nav-account-settings-btn"
              >
                <span class="dropdown-item" @click="authenticationStore.modal = true"
                  >Account Settings</span
                >
              </RouterLink>
            </li>
            <li style="cursor: pointer">
              <span class="dropdown-item" @click="logout()">Log Out</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </nav>

  <!-- QuickBooks connection modal -->
  <v-dialog v-model="qbModalOpen" max-width="500">
    <v-card>
      <v-card-title class="d-flex align-center gap-3 pa-4">
        <QuickbooksIcon :connected="qbConnected" :size="28" />
        <span>QuickBooks Connection</span>
        <v-spacer />
        <v-btn icon variant="plain" @click="qbModalOpen = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-5">
        <!-- Loading -->
        <div v-if="qbStatus.loading" class="d-flex align-center gap-3 py-4">
          <v-progress-circular indeterminate color="green" size="22" />
          <span>Checking QuickBooks connection…</span>
        </div>

        <!-- Status -->
        <template v-else>
          <div class="qb-status-row mb-4">
            <v-icon :color="qbConnected ? 'green' : 'red'" size="20">
              {{ qbConnected ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span>
              {{ qbConnected ? 'Connected to QuickBooks' : 'Not connected to QuickBooks' }}
            </span>
            <v-chip v-if="qbStatus.data?.environment" size="x-small" variant="tonal" color="gray">
              {{ qbStatus.data.environment }}
            </v-chip>
          </div>

          <div v-if="qbStatus.data?.realm_id" class="qb-status-meta mb-1">
            <span class="qb-label">Realm ID</span>
            <span>{{ qbStatus.data.realm_id }}</span>
          </div>

          <div v-if="qbStatus.data?.issues?.length" class="mt-3">
            <div v-for="issue in qbStatus.data.issues" :key="issue" class="qb-issue">
              <v-icon size="16" color="orange">mdi-alert-outline</v-icon>
              {{ issue }}
            </div>
          </div>

          <!-- OAuth connect/reconnect button -->
          <v-btn
            class="mt-5"
            :color="qbConnected ? 'gray' : 'green'"
            variant="tonal"
            :loading="qbStatus.redirecting"
            block
            @click="startOAuth"
          >
            <v-icon start>mdi-link-variant</v-icon>
            {{ qbConnected ? 'Reconnect QuickBooks' : 'Connect QuickBooks' }}
          </v-btn>

          <v-btn
            class="mt-2"
            variant="text"
            size="small"
            :loading="qbStatus.loading"
            block
            @click="loadQbStatus"
          >
            <v-icon start size="16">mdi-refresh</v-icon>
            Refresh status
          </v-btn>
        </template>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script lang="ts" setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useAuthenticationStore } from "@/stores/authentication";
import Logo from "@/assets/icons/logo-vertical-knockout.svg";
import QuickbooksIcon from "@/components/support/QuickbooksIcon.vue";
import FetchService from "@/services/fetch.service";

const authenticationStore = useAuthenticationStore();
const tour = ref<any>(null);

// ── QuickBooks status ───────────────────────────────────────────────
const qbModalOpen = ref(false);
const qbStatus = reactive({
  loading: false,
  redirecting: false,
  data: null as Record<string, any> | null,
});

const qbConnected = computed(() => qbStatus.data?.connected === true);
const isQbAdmin = computed(() => authenticationStore.jwtData?.uid === 'e4a3f59cdcd7bcf564ea8fbe68af982d');

async function loadQbStatus() {
  qbStatus.loading = true;
  try {
    const res = await FetchService.fetchData("/quickbooks/status", "get");
    qbStatus.data = res ?? null;
  } catch {
    qbStatus.data = null;
  } finally {
    qbStatus.loading = false;
  }
}

async function startOAuth() {
  qbStatus.redirecting = true;
  try {
    // Fetch the OAuth URL from the API and open it in a new tab
    const res = await FetchService.fetchData("/quickbooks/oauth-url", "get");
    if (res?.url) {
      window.open(res.url, "_blank", "width=600,height=700,noopener");
    }
  } finally {
    qbStatus.redirecting = false;
  }
}

// Load status when modal opens
import { watch } from "vue";
watch(qbModalOpen, (open) => { if (open) loadQbStatus(); });

// Load on mount to show green/gray icon immediately — only for QB admin
onMounted(() => {
  if (isQbAdmin.value) loadQbStatus();
});

// ── Auth ────────────────────────────────────────────────────────────
function logout() {
  authenticationStore.logout();
  window.location.pathname = "/support/login";
}
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;
@use "@/assets/_breakpoints.scss" as breakpoints;

.school-navbar {
  padding: 10px 15px;
  display: flex;
  background: linear-gradient(#4c1216, #721a1e 20%, #4b1216);

  .nav-container {
    display: flex;
    justify-content: space-between;
    width: 100%;

    .select-event {
      height: fit-content;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      flex-direction: column;
      button {
        margin: 0;
        padding: 0;
      }
    }

    .nav-right {
      display: flex;
      align-items: center;
      gap: 20px;

      :deep(.v-select__selection-text) {
        color: white;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .help {
        display: flex;
        align-items: center;
        gap: 7px;
        color: white;
        padding: 5px 10px;
        border-radius: 4px;
        transition: background-color 0.2s linear;

        &:hover {
          background-color: rgba(0, 0, 0, 0.134);
        }
        &:active {
          background-color: rgba(0, 0, 0, 0.234);
        }
      }
    }
  }
}

.qb-status-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.qb-status-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #666;
}

.qb-label {
  font-weight: 500;
  color: #444;
  min-width: 70px;
}

.qb-issue {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #b45309;
  margin-bottom: 4px;
}
</style>
